/* eslint-disable max-lines */
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Signal,
  signal,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { FormGroup } from '@angular/forms';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { firstValueFrom, lastValueFrom, map, race, take } from 'rxjs';

import { TranslatePipe, I18nService } from '@core/i18n-legacy';
import { injectDevFlagSignal } from '@core/tellent-dev-flags-logic';
import { TellentCurrentUserDataFacade, TellentMembersDataFacade } from '@core/tellent-logic';
import { TellentDevFlag, TellentMember, TeamMemberStatus } from '@core/tellent-types';
import { TellentComponentsNotificationService, resolveMemberFullName } from '@core/tellent-util';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogRef,
  DialogService,
  DialogWidth,
  FormFieldComponent,
  HeadingComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
  ProtectedControlDirective,
  RtPlatform,
} from '@core/user-interface-legacy';

import { EditMemberEmailDialogComponent } from './edit-member-email-dialog/edit-member-email-dialog.component';
import { AvatarInputComponent } from '../../avatar-input/avatar-input.component';
import { ConfirmLoginChangeDialogComponent } from '../../current-user-profile-dialog/current-user-profile/confirm-login-change-dialog/confirm-login-change-dialog.component';

interface TellentEditMemberForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  login: FormControl<string>;
  personalEmail: FormControl<string>;
}

/*
 eslint-disable
  @typescript-eslint/consistent-type-assertions,
 @angular-eslint/no-inputs-metadata-property,
*/

@Component({
  selector: 'edit-member-account-tab',
  imports: [
    FormFieldComponent,
    InputComponent,
    ButtonComponent,
    ReactiveFormsModule,
    LabelComponent,
    DialogFooterComponent,
    TranslatePipe,
    DialogBodyComponent,
    HeadingComponent,
    IconComponent,
    AvatarInputComponent,
    ButtonLoadingDirective,
    ProtectedControlDirective,
  ],
  templateUrl: './edit-member-account-tab.component.html',
  styleUrl: './edit-member-account-tab.component.less',
  inputs: EditMemberAccountTabComponent.NgInputs,
})
export class EditMemberAccountTabComponent {
  public static NgInputs = ['memberGuid' as const];

  private readonly formBuilder = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(DialogRef);
  private readonly dialogService = inject(DialogService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly actions = inject(Actions);
  private readonly notificationsService = inject(TellentComponentsNotificationService);
  private readonly platform = inject(RtPlatform);
  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly i18n = inject(I18nService);

  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly TeamMemberStatus = TeamMemberStatus;

  public memberGuid: string;

  public memberData: Signal<TellentMember | null>;
  public memberStatus: Signal<TellentMember['status'] | null>;
  public memberVerified: Signal<boolean | null> = signal(false);

  public me = this.currentUserFacade.currentUser;

  public pending = signal(false);
  public emailErrorMessage = signal<string | null>(null);
  public personalEmailErrorMessage = signal<string | null>(null);

  public form = this.formBuilder.nonNullable.group<TellentEditMemberForm>({
    firstName: new FormControl<string>('', { nonNullable: true }),
    lastName: new FormControl<string>('', { nonNullable: true }),
    email: new FormControl<string>('', { validators: Validators.email, nonNullable: true }),
    login: new FormControl<string>('', { nonNullable: true }),
    personalEmail: new FormControl<string>('', { nonNullable: true }),
  });

  public avatarFile: File | null;

  protected usersWithoutEmails = injectDevFlagSignal(TellentDevFlag.UsersWithoutEmails);

  protected hasWorkEmail = computed(() => {
    if (!this.usersWithoutEmails()) {
      return true; // Legacy behavior
    }
    return !!this.memberData()?.hasWorkEmail;
  });

  protected usesUsernameLogin = computed(() => {
    if (!this.usersWithoutEmails()) {
      return false; // Legacy behavior
    }
    const memberData = this.memberData();
    return !memberData?.hasWorkEmail && !!memberData?.login;
  });

  protected isNotInvited = computed(() => {
    if (!this.usersWithoutEmails()) {
      return false; // Legacy behavior
    }
    return this.memberStatus() === TeamMemberStatus.NotInvited;
  });

  protected updateFormWithUserData = effect(() => {
    const memberData = this.memberData()!;
    const { firstName, lastName, email, login, personalEmail, hasWorkEmail } = memberData;

    this.form.patchValue(
      {
        firstName,
        lastName,
        email: hasWorkEmail ? email : '',
        login,
        personalEmail,
      },
      { emitEvent: false },
    );
  });

  private updateStatus = effect(() => {
    this.disableFormFields();
    this.cdRef.detectChanges();
  });

  private fullName = computed(() => resolveMemberFullName(this.memberData()!));

  public ngOnInit() {
    this.memberData = this.membersDataFacade.teamMemberById(this.memberGuid);

    this.memberStatus = computed(() => this.memberData()?.status!);
    this.memberVerified = computed(
      () =>
        this.memberStatus() === TeamMemberStatus.Active ||
        this.memberStatus() === TeamMemberStatus.Deactivated,
    );

    this.disableFormFields();
    this.subscribeAndHandleError();
  }

  public subscribeAndHandleError(): void {
    this.membersDataFacade.updateMemberFail$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(error => {
        const errors = error.payload?.error?.errors;

        const handleEmailError = (errorField: 'email' | 'personalEmail') => {
          const errorMessage = errors[errorField]?.[0];
          if (errorMessage === 'has already been taken') {
            const translatedMessage = this.i18n.translate(
              'tlc.members.edit_member.account_tab.section.email.errors.already_taken',
            );
            const signalToUpdate =
              errorField === 'email' ? this.emailErrorMessage : this.personalEmailErrorMessage;
            signalToUpdate.set(translatedMessage);
          }
        };

        if (errors?.email) handleEmailError('email');
        if (errors?.personalEmail) handleEmailError('personalEmail');
      });
  }

  public async submit() {
    this.emailErrorMessage.set(null);
    this.personalEmailErrorMessage.set(null);
    this.pending.set(true);
    const results = await this.submitFormUpdate(this.form, this.avatarFile);

    if (results !== undefined && results.success) {
      this.dialogRef.close(results);
    } else {
      this.pending.set(false);
    }
  }

  public cancel() {
    this.dialogRef.close();
  }
  public changeEmailClicked() {
    this.dialogService.openCustom(
      EditMemberEmailDialogComponent,
      {
        width: DialogWidth.SmallXX,
        mobileOptions: this.platform.IS_MOBILE
          ? {
              mobileModal: true,
            }
          : undefined,
      },
      { memberId: this.memberGuid },
      this.viewContainerRef,
    );
  }

  public cacheAvatarImg(file: File | null): void {
    this.avatarFile = file;
    this.setFormToDirty();
  }

  private async submitFormUpdate(form: FormGroup<TellentEditMemberForm>, avatar?: File | null) {
    const { firstName, lastName } = form.value;

    const hasLoginChangeBeenConfirmed = await this.confirmLoginChange(form);

    if (!hasLoginChangeBeenConfirmed) {
      this.pending.set(false);
      return undefined;
    }

    this.membersDataFacade.updateMember({
      firstName: firstName!,
      lastName: lastName!,
      email: form.controls.email.dirty ? form.value.email : undefined,
      login: form.controls.login.dirty ? form.value.login : undefined,
      personalEmail: form.controls.personalEmail.dirty ? form.value.personalEmail : undefined,
      avatar: avatar ? avatar : undefined,
      guid: this.memberGuid,
    });

    const result$ = race(
      this.membersDataFacade.updateMemberSuccess$,
      this.membersDataFacade.updateMemberFail$,
    ).pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
      map(({ payload }) => {
        form.markAsPristine();
        return {
          success: !(payload instanceof HttpErrorResponse),
          payload,
        };
      }),
    );

    return lastValueFrom(result$);
  }

  private hasLoginChanged(form: FormGroup<TellentEditMemberForm>) {
    const loginControl = form.controls.login;
    return loginControl.dirty && loginControl.value !== this.currentUserFacade.currentUser()?.login;
  }

  private async confirmLoginChange(form: FormGroup<TellentEditMemberForm>): Promise<boolean> {
    const loginChanged = this.hasLoginChanged(form);
    if (loginChanged) {
      const { afterClosed } = this.dialogService.openCustom(
        ConfirmLoginChangeDialogComponent,
        {
          width: DialogWidth.SmallX,
        },
        { login: form.controls.login.value, changeByAdmin: true },
      );
      const result = await firstValueFrom(afterClosed);

      if (!result) {
        return false;
      }
    }
    return true;
  }

  private disableFormFields() {
    this.memberVerified() ? this.form.controls.email.disable() : this.form.controls.email.enable();
    this.memberStatus() === TeamMemberStatus.Deactivated
      ? this.form.controls.firstName.disable()
      : this.form.controls.firstName.enable();
    this.memberStatus() === TeamMemberStatus.Deactivated
      ? this.form.controls.lastName.disable()
      : this.form.controls.lastName.enable();
  }

  private setFormToDirty() {
    this.form.markAsDirty();
  }
}
